import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGBasicSelect } from "../../../MainComponents/Select/EGBasicSelect";
import { EGInput } from "../../../MainComponents/Input/EGInput";
import { EGButton } from "../../../MainComponents/Button/EGButton/EGButton";
import {
  getInfographics,
  getPredefinedMessages,
  toMessageAttachment,
} from "../../../../helpers/complainantMessages";

/**
 * Compositor de la conversacion con el denunciante.
 *
 * A diferencia del chat interno, aqui NO se escribe libre ni se suben archivos:
 * solo se elige un mensaje predisenado y/o infografias del catalogo MSG-100.
 * Es una decision de control sobre lo que sale de la organizacion hacia quien
 * reporto.
 *
 * Se puede enviar un mensaje, infografias, o ambos; al menos una de las dos.
 */
export const ComplainantComposer = ({ onSend }) => {
  const [t] = useTranslation("messages");
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [infographics, setInfographics] = useState([]);

  const [selectedMessage, setSelectedMessage] = useState("");
  const [selectedInfographics, setSelectedInfographics] = useState([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([getPredefinedMessages(), getInfographics()])
      .then(([messageOptions, infographicOptions]) => {
        if (!mounted) return;
        setMessages(messageOptions);
        setInfographics(infographicOptions);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const message = messages.find((item) => item.value === selectedMessage);
  const canSend =
    !isSending && (!!message || selectedInfographics.length > 0);

  const handleSend = async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      // Se envia el TEXTO, no la referencia al catalogo: lo que recibio el
      // denunciante no debe cambiar si despues se edita el catalogo.
      await onSend({
        content: message?.text || "",
        attachments: selectedInfographics.map(toMessageAttachment),
      });
      setSelectedMessage("");
      setSelectedInfographics([]);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" p={1.5}>
        <CircularProgress size={16} />
        <Typography variant="body2">{`${t("messages.loading")}...`}</Typography>
      </Stack>
    );
  }

  const hasCatalogue = messages.length > 0 || infographics.length > 0;

  if (!hasCatalogue) {
    return (
      <Box p={1.5}>
        <Typography
          component="div"
          fontSize={12}
          lineHeight={1.4}
          fontStyle="italic"
          color={theme.palette.blueGray500.main}
        >
          {t("messages.no_predefined_available")}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5} p={1.5} width="100%">
      {messages.length > 0 && (
        <Box>
          <EGBasicSelect
            label={`${t("messages.predefined_message")}:`}
            options={messages}
            value={selectedMessage}
            onChange={(e) => setSelectedMessage(e.target.value)}
          />
          {/* Se muestra el texto exacto que va a recibir el denunciante. */}
          {message && (
            <EGInput
              sx={{ mt: 1 }}
              label={`${t("messages.will_be_sent")}:`}
              value={message.text}
              multiline
              rows={3}
              disabled
            />
          )}
        </Box>
      )}

      {infographics.length > 0 && (
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={infographics}
          getOptionLabel={(option) => option.label}
          value={selectedInfographics}
          onChange={(_, selected) => setSelectedInfographics(selected)}
          isOptionEqualToValue={(option, selected) =>
            option.value === selected.value
          }
          renderTags={(tags, getTagProps) =>
            tags.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                sx={{
                  backgroundColor: theme.palette.blueGray100.main,
                  fontSize: 12,
                }}
              />
            ))
          }
          renderOption={(props, option, { selected }) => (
            <Box {...props} key={option.value}>
              <Checkbox size="small" checked={selected} />
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          )}
          renderInput={(params) => (
            <EGInput
              {...params}
              label={`${t("messages.infographics")}:`}
              placeholder={t("messages.select_infographics")}
            />
          )}
        />
      )}

      <Box display="flex" justifyContent="flex-end">
        <EGButton
          label={t("messages.send")}
          iconName="send"
          color={canSend ? "success" : "light"}
          iconColor={canSend ? "white" : ""}
          iconColorHover={canSend ? "white" : ""}
          labelColor={canSend ? "white" : ""}
          disabled={!canSend}
          loading={isSending}
          onClick={handleSend}
        />
      </Box>
    </Stack>
  );
};
